/**
 * THE TEXT EDIT RING — the mark drawn around the element a user is currently typing into, inside an
 * artifact on the preview stage.
 *
 * A hairline box and a small badge hanging underneath. It is a PICTURE OF A STATE and not a control:
 * the caret lives inside the artifact's own frame, every press on this rectangle belongs to that
 * frame, and so the whole component is `pointer-events: none` with nothing to click, drag or focus.
 * It reports where the caret is; it does not move it.
 *
 * ## No corner handles HERE
 *
 * It was built with four, off the board, and they came off because they LIED: nothing could be
 * resized. Now something can, and the handles that do it live in `LayoutEditLayer`, drawn over this
 * same rect on the same stroke line. This component stays the picture — `pointer-events: none`, the
 * hairline and the badge — and the layer is the control, so each can change without the other.
 *
 * ## The one constraint that shapes the file
 *
 * It mounts INSIDE `ArtifactFrame`'s `.canvas` — the artifact's own 1920x1080 coordinate space,
 * carrying the pane's single `transform: scale()`. So every number written here is painted at
 * `scale`, and at Fit (a 1920 artifact in a ~760 pane, so ~0.4) a 1.5px stroke paints at 0.6px and a
 * 17px badge at 7px: an unreadable smear of blue.
 *
 * `AnnotateLayer` hit this exact wall and its answer is ported wholesale rather than reinvented:
 * `zoom = clamp(1, 1/scale, 3)`, published as one custom property, and every CHROME measure
 * multiplied by it while GEOMETRY stays in natural units. Same clamp, same split, different prefix
 * — `--d15e-*` to that layer's `--d15a-*`, so the two can be mounted over the same artifact without
 * either one reaching into the other's numbers.
 *
 * What did NOT need to come along is the machinery around it. That layer has no `scale` prop — it is
 * a pointer surface, so it has to know its own on-screen size to convert clicks — and it discovers
 * it by polling `getBoundingClientRect` every 300ms, because its LAYOUT box never changes when the
 * frame zooms and a `ResizeObserver` is therefore blind to the thing it needs to watch. This ring is
 * HANDED the scale by the host that already computed it (`useArtifactFit`), so the zoom here is a
 * pure function of a prop: no ref, no poll, no effect, nothing to tear down, and no frame in which
 * the ring is drawn at a zoom the stage has already left.
 *
 * ## No `theme` prop
 *
 * Blue on the artifact in both themes — the same call `RegionReference` makes about a crop laid over
 * a screenshot. The mark sits on content the app neither owns nor can predict, so the thing it has
 * to stand out against is a document, not a shell; a ring that went pale because the app went light
 * would be the app theming the document. `--color-blue-500` is a base token (`@theme static`, no
 * light/dark variant), so that claim holds mechanically and not merely by intention.
 */
import type { CSSProperties } from 'react';
import { cn } from '~/lib/utils';
import styles from './TextEditRing.module.css';

export interface TextEditRingProps {
  /** The element's box in the artifact's own coordinate space (1920x1080). */
  rect: { x: number; y: number; w: number; h: number };
  /**
   * The frame's current scale — rendered width over natural width.
   *
   * OPTIONAL, and normally omitted. Mounted where it belongs — inside `ArtifactFrame`'s canvas — the
   * ring reads the scale the frame already publishes as `--hv15-art-scale` and needs nothing from the
   * host, which is just as well: the host does not have the number, `useArtifactFit` computes it
   * inside the pane. Pass it only to drive the ring somewhere there is no frame, which in practice
   * means the stories.
   */
  scale?: number;
  /**
   * What a badge under the ring says. No label, no badge: the outline alone, which is all /deck and the brand
   * kit draw (Beka, 2026-09-27: no sizes, and no "editing" either). Only the landing page's demo labels it.
   */
  label?: string;
  className?: string;
}

/**
 * The counter-scale: the same clamp `AnnotateLayer`'s `zoomFor` applies, which is itself the
 * annotation SDK's `Math.min(3, Math.max(1, z))` verbatim.
 *
 * Copied rather than imported because it is not exported over there, and reaching into that module
 * for six lines would drag the annotation bus and the frame hit-test bridge into everything that
 * wants to draw a ring.
 *
 * The clamp is the whole design, at both ends. Below 1 — zoomed IN past 100% — chrome would start to
 * SHRINK, which is backwards: at 200% the artifact is twice the size and the ring should be exactly
 * as heavy as it always was, not half as heavy. Above 3 — an artifact fitted into a thumbnail —
 * chrome would swamp the thing it is measuring. The guard on the input is not defensive padding: a
 * scale of 0 is a real value on the frame's first paint, before anything has been measured.
 */
function zoomFor(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 0) return 1;
  return Math.min(3, Math.max(1, 1 / scale));
}

export function TextEditRing({ rect, scale, label, className }: TextEditRingProps) {
  return (
    <div
      className={cn(styles.ring, className)}
      // Decoration for assistive tech, on `RegionReference`'s reasoning: the badge says in one word
      // what the caret already says by being where it is, and the caret is inside the artifact's own
      // frame with its own accessibility tree. Announcing this rectangle would say it twice, to a
      // reader who cannot reach it — the ring is `pointer-events: none` and holds nothing focusable.
      aria-hidden="true"
      style={
        {
          // The only runtime values in the component, and the only two kinds there are: the rect,
          // which is geometry, and the zoom, which every chrome measure in the stylesheet multiplies
          // itself by. Nothing else is styled from here.
          '--d15e-x': `${rect.x}px`,
          '--d15e-y': `${rect.y}px`,
          '--d15e-w': `${rect.w}px`,
          '--d15e-h': `${rect.h}px`,
          // Only when the caller actually knows. Left unset, the stylesheet's own default reads the
          // scale off the frame — see `.ring`. Writing `1` here instead would silently defeat that,
          // because an inline custom property beats the rule that would have derived it.
          ...(scale === undefined ? null : { '--d15e-zoom': `${zoomFor(scale)}` }),
        } as CSSProperties
      }
    >
      {label ? (
        <div className={styles.badge}>
          <span className={styles.pill}>{label}</span>
        </div>
      ) : null}
    </div>
  );
}
